import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowBendUpLeft, PencilSimple, Trash } from '@phosphor-icons/react';
import { AlertCircle, Loader2, MessageCircle, RefreshCw, Send, X } from '../../../icons';
import { Button } from '../../ui/button';

import { cn } from '../../../lib/utils';
import type { MessageAnchor, ReviewThreadEntry } from '../../../lib/models';
import { ScrollArea } from '../../ui/scroll-area';
import { Skeleton } from '../../ui/skeleton';
import { ActionConfirmDialog } from '../ActionConfirmDialog';

export type ChatThreadEntry = ReviewThreadEntry;

/** What sending a message hands the wrapping panel. An object, not
 *  positional args: `anchor` sits in the middle of the old (body, anchor,
 *  replyTo) signature, so a caller that doesn't support anchors (Content
 *  Review, Creator Review) had to name-and-discard it (`(body, _anchor,
 *  replyTo) => ...`) just to reach `replyTo` — easy to typo/reorder and
 *  silently drop the wrong field. A named field can't be positionally
 *  confused with another, and an adapter that doesn't care about `anchor`
 *  just never destructures it. */
export type AddCommentPayload = { body: string; anchor?: MessageAnchor; replyTo?: string };

export type ChatThreadProps = {
  entries?: ChatThreadEntry[];

  /** Header label above the messages (e.g. "Conversation"). When provided,
   *  renders a Figma-styled header strip (18px/500). Wrapping panels that
   *  provide their own header should leave this unset. */
  title?: string;

  /** Optional close handler — renders an X button inside the title header.
   *  Typically used to collapse the chat column from within the thread. */
  onClose?: () => void;

  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;

  canAddComment?: boolean;
  /** Thread is locked (approved → read-only): nothing on it can be posted,
   *  edited or deleted. Distinct from `canAddComment`, which answers "may THIS
   *  viewer post?" — a role-based posting restriction must not also revoke the
   *  viewer's ability to edit or delete their own past messages on an open
   *  thread. Omitted: falls back to `canAddComment === false`, which is what
   *  every current caller means by it. */
  locked?: boolean;
  addCommentBusy?: boolean;
  onAddComment?: (payload: AddCommentPayload) => void | Promise<void>;

  /** Quote-reply anchor staged above the composer (script annotations).
   *  Owned by the wrapping panel; sent with the next message. */
  stagedAnchor?: MessageAnchor;
  /** Dismisses the staged anchor — called by the chip's × (no argument)
   *  and after a successful anchored send (with the SENT anchor, so the
   *  owner only clears it if the user hasn't staged a new one since). */
  onDismissAnchor?: (sent?: MessageAnchor) => void;
  /** Clicking a message's quote-reply anchor block jumps the script view
   *  to that line (and highlights it). Omit to render the quote as plain,
   *  non-interactive text — e.g. surfaces with no script pane to jump to. */
  onJumpToAnchor?: (anchor: MessageAnchor) => void;

  /** The reverse direction of onJumpToAnchor: an external trigger (the
   *  Script tab's "View comment" hover pill) asking THIS thread to scroll
   *  to and flash one of its own messages — reuses the exact scroll+flash
   *  jumpToMessage already does internally for a reply-preview click.
   *  Nonce-keyed so clicking the SAME message twice in a row still
   *  re-triggers (a same-id update alone is a no-op React bails on). */
  scrollToMessageId?: { id: string; nonce: number } | null;

  /** Enables reply-to nesting: a Reply affordance on bubbles and
   *  single-level indented rendering of replies under their root.
   *  Off by default — panels whose send handlers don't forward replyTo
   *  must not show a Reply button that would silently flatten. */
  enableReplies?: boolean;

  /** Read-only viewer: keep the composer visible but disable the input +
   *  send button and surface a tooltip. Distinct from the locked state
   *  (`canAddComment === false`), which hides the composer entirely. */
  readOnly?: boolean;

  draft?: string;
  setDraft?: (v: string) => void;
  /** Composer is an auto-growing `<textarea>` (1–4 lines, then scrolls). */
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;

  outgoingActor?: string;

  /** True only for the Super Admin tier. Super Admin's delete is
   *  unconditional server-side, including on a locked (approved) thread —
   *  the only role for which that's true. Every other viewer's delete/edit
   *  must still gate on the local `threadLocked`/`readOnly` state so the
   *  button doesn't round-trip a 423. */
  viewerIsSuperAdmin?: boolean;
  /** Edit a message (body only). Per-message eligibility is entirely
   *  server-computed (entry.viewer_can_edit) — omit to hide Edit everywhere. */
  onEditMessage?: (id: string, body: string) => void | Promise<void>;
  /** Delete a message. Per-message eligibility is entirely server-computed
   *  (entry.viewer_can_delete) — omit to hide Delete everywhere. */
  onDeleteMessage?: (id: string) => void | Promise<void>;

  emptyStateText?: string;
  composerPlaceholder?: string;
  className?: string;
};

// "Brand" is one logical side regardless of which sub-role
// (brand_owner / brand_member / brand_manager) actually authored the
// message. Same idea for creator + their talent_manager, and for the three
// admin-tier roles (admin / admin_member / super_admin) — without this, one
// admin teammate's message would render on the *other* side (as if it were
// the brand's) when a different admin-tier viewer looks at the same thread.
function normalizeSide(actor: string): string {
  if (!actor) return 'system';
  if (actor === 'brand' || actor.startsWith('brand_')) return 'brand';
  if (actor === 'creator' || actor === 'talent_manager') return 'creator';
  if (actor === 'admin' || actor === 'admin_member' || actor === 'super_admin') return 'admin';
  return actor;
}

function isMine(actor: string, mine: string): boolean {
  return normalizeSide(actor) === normalizeSide(mine);
}

function actorLabel(actor: string): string {
  switch (actor) {
    case 'brand':
    case 'brand_owner':
      return 'Owner';
    case 'brand_admin':
      return 'Admin';
    case 'brand_member':
      return 'Brand Member';
    case 'admin':
    case 'admin_member':
    case 'super_admin':
      return 'Discovr Team';
    case 'creator':
      return 'Creator';
    case 'talent_manager':
      return 'Talent Manager';
    case 'ai':
      return 'AI';
    case 'legacy':
      return 'Note';
    default:
      return actor || 'System';
  }
}

/** The label shown for a message's sender: their name plus org — e.g.
 *  "Samyak Rakhecha · TechWith" for a brand user, "Priya · Discovr Team" for
 *  an admin (the BE stamps the brand name / team label into sender_title).
 *  Falls back to the org alone when the name is blank (an account with no
 *  first/last name), then to a role-derived label when neither is set. The
 *  caller substitutes "You" for the viewer's own messages. */
function senderDisplayName(entry: {
  actor: string;
  sender_name?: string;
  sender_title?: string;
}): string {
  const person = entry.sender_name?.trim();
  const org = entry.sender_title?.trim();
  if (person && org) return `${person} · ${org}`;
  return person || org || actorLabel(entry.actor);
}

function formatTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, now)) return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export function ChatThread({
  entries,
  title,
  onClose,
  isLoading = false,
  isError = false,
  onRetry,
  canAddComment,
  locked,
  addCommentBusy,
  onAddComment,
  stagedAnchor,
  onDismissAnchor,
  onJumpToAnchor,
  scrollToMessageId,
  enableReplies = false,
  readOnly = false,
  draft: draftProp,
  setDraft: setDraftProp,
  textareaRef: textareaRefProp,
  outgoingActor = 'brand',
  viewerIsSuperAdmin = false,
  onEditMessage,
  onDeleteMessage,
  emptyStateText = 'No messages yet. Start the conversation by leaving a note below.',
  composerPlaceholder = 'Type a message…',
  className,
}: ChatThreadProps) {
  // Explicit `locked` wins; otherwise keep the historical reading of
  // canAddComment as the lock signal so existing callers are unaffected.
  const threadLocked = locked ?? !canAddComment;
  // That fallback is the trap this prop exists to remove: a caller whose
  // canAddComment encodes anything other than lock state (a role restriction, a
  // "is a post handler wired?" check) silently loses Edit/Delete on an open
  // thread. `locked` is optional for back-compat, so TypeScript cannot demand
  // it — surface it at dev time instead of failing silently. Ref-guarded in an
  // effect so it fires once per offending mount, not on every keystroke.
  const warnedMissingLockedRef = useRef(false);
  const missingLocked = locked === undefined && !!(onEditMessage || onDeleteMessage);
  useEffect(() => {
    if (!import.meta.env?.DEV || !missingLocked || warnedMissingLockedRef.current) return;
    warnedMissingLockedRef.current = true;
    console.warn(
      '[ChatThread] onEditMessage/onDeleteMessage supplied without `locked`; ' +
        'falling back to `!canAddComment`. Pass an explicit `locked` — if this ' +
        "caller's canAddComment is not purely lock state, Edit/Delete will be " +
        'hidden on an unlocked thread.',
    );
  }, [missingLocked]);
  const [internalDraft, setInternalDraft] = useState('');
  const internalRef = useRef<HTMLTextAreaElement | null>(null);
  const draft = draftProp ?? internalDraft;
  const setDraft = setDraftProp ?? setInternalDraft;
  const [expandedMsgs, setExpandedMsgs] = useState<Set<string>>(() => new Set());
  const toggleExpanded = (id: string): void =>
    setExpandedMsgs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const textareaRef = textareaRefProp ?? internalRef;
  // Auto-grow the composer with content (1–4 lines), then switch to its own
  // scrollbar — re-measured on every draft change, not just onChange, so a
  // draft set externally (e.g. "Add to comment") grows it too.
  //
  // The 4-line cap is computed from the textarea's own metrics rather than
  // a fixed px class: a hardcoded max-height only happens to fit N lines
  // for one exact combination of font-size/line-height/padding, and
  // silently clips a line short the moment any of those changes.
  const COMPOSER_MAX_ROWS = 4;
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // border-box sizing: `height` is the OUTER box (content + padding +
    // border), but scrollHeight is the inner box (content + padding only).
    // Setting height straight to scrollHeight comes up border-width short —
    // content then overflows by that same amount, showing a permanent
    // sliver scrollbar even on an empty, single-line textarea.
    const cs = getComputedStyle(el);
    const borderY = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const paddingY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const lineHeight = parseFloat(cs.lineHeight);
    const maxHeight = lineHeight * COMPOSER_MAX_ROWS + paddingY + borderY;
    const contentHeight = el.scrollHeight + borderY;
    el.style.height = `${Math.min(contentHeight, maxHeight)}px`;
    el.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  }, [draft, textareaRef]);

  const [optimistic, setOptimistic] = useState<ChatThreadEntry[]>([]);
  // Last send failed — surfaced above the composer (a silent catch made
  // rejected sends look like the feature was broken). Cleared on typing.
  const [sendFailed, setSendFailed] = useState(false);
  // Reply target staged from a bubble's Reply button; rides the next send.
  const [stagedReply, setStagedReply] = useState<{ id: string; label: string; preview: string } | null>(null);
  // Message being edited: its ORIGINAL body is previewed in the composer
  // banner (WhatsApp-style) and loaded into the input for editing; the next
  // submit rewrites it in place instead of sending a new message.
  const [editing, setEditing] = useState<{ id: string; preview: string } | null>(null);
  // Message pending delete confirmation — drives the in-app confirm dialog
  // (no native window.confirm) and its in-flight state.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const threadContentRef = useRef<HTMLDivElement | null>(null);
  const prevThreadLenRef = useRef(0);
  // Enter-to-send has no disabled state (unlike the Send button), so an
  // in-flight guard prevents a double Enter from sending the same anchor
  // on two messages.
  const sendingRef = useRef(false);

  // WhatsApp-style "tap the quoted preview to jump to the original message":
  // one-shot flash, cleared on its own timer — not driven by scrollToQuote
  // state like the script-anchor jump, since this never needs to survive a
  // tab switch or an external trigger, only a click inside this thread.
  //
  // Keyed by a NONCE, not just the target id: clicking the SAME preview
  // again while the previous flash is still mid-animation set state to the
  // same id, which React bails on (no re-render) — the overlay div was
  // never remounted, so its CSS `animation` never restarted, but the
  // timeout clearing it away kept getting pushed out by each click. Once
  // the (never-restarted) animation naturally finished at 1.6s but the div
  // was still up (deletion delayed by a later click), it had no
  // in-progress animation left to hold it at opacity 0 — it reverted to
  // its unanimated base, fully opaque, painting a solid dark purple block
  // over the message. The nonce forces a fresh `key` on every click, so
  // React always remounts a brand-new div with its animation starting
  // clean, however rapid the clicks.
  const [flashTarget, setFlashTarget] = useState<{ id: string; nonce: number } | null>(null);
  const flashNonceRef = useRef(0);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jumpToMessage = (id: string) => {
    const target = threadContentRef.current?.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(id)}"]`,
    );
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Move focus too, not just the scroll position — a keyboard/AT user
    // who activates this control (vs. a mouse user who visually sees the
    // scroll + flash) otherwise has focus stay on the control they just
    // pressed, with no indication anything happened. Needs tabIndex={-1}
    // on the target (see data-message-id below) to be a valid focus target
    // despite not being normally tab-reachable.
    target.focus({ preventScroll: true });
    if (flashTimeoutRef.current !== null) clearTimeout(flashTimeoutRef.current);
    flashNonceRef.current += 1;
    setFlashTarget({ id, nonce: flashNonceRef.current });
    const FLASH_MS = 1600;
    flashTimeoutRef.current = setTimeout(() => {
      flashTimeoutRef.current = null;
      setFlashTarget(null);
    }, FLASH_MS);
  };
  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current !== null) clearTimeout(flashTimeoutRef.current);
    };
  }, []);

  // External trigger for jumpToMessage (the Script tab's "View comment"
  // hover pill) — see scrollToMessageId's own doc comment. Depends on the
  // prop's own object identity, not its contents: the owner (ScriptReviewPanel)
  // already constructs a fresh {id, nonce} on every click, even a repeat
  // click on the same message, so identity alone is enough to retrigger —
  // same convention as ScriptResultTabs' scrollToQuote.
  useEffect(() => {
    if (!scrollToMessageId) return;
    jumpToMessage(scrollToMessageId.id);
  }, [scrollToMessageId]);

  useEffect(() => {
    const len = Array.isArray(entries) ? entries.length : 0;
    if (len > prevThreadLenRef.current) {
      setOptimistic([]);
    }
    prevThreadLenRef.current = len;
  }, [entries]);

  const sorted = useMemo(() => {
    const list = Array.isArray(entries) ? [...entries] : [];
    const ids = new Set(list.map((e) => e.id));
    for (const e of optimistic) if (!ids.has(e.id)) list.push(e);
    list.sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
    });
    return list;
  }, [entries, optimistic]);

  // WhatsApp-style replies: strict chronological order; a reply carries a
  // compact quoted preview of its parent INSIDE the bubble (both sides).
  // reply_to pointing outside the loaded page simply shows no preview.
  const byId = useMemo(() => new Map(sorted.map((m) => [m.id, m] as const)), [sorted]);

  // Opening a thread with existing history jumps straight to the bottom
  // (instant) — same as WhatsApp/Slack, no reason to animate through a
  // long backlog on first paint. A message arriving AFTER that (the
  // user's own send, a reply, an SSE push) scrolls smoothly instead, so
  // it reads as a natural continuation of the conversation rather than a
  // jarring jump.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      block: 'end',
      behavior: hasMountedRef.current ? 'smooth' : 'auto',
    });
    hasMountedRef.current = true;
  }, [sorted.length]);

  // Load a message into the composer for an in-place edit. Clears any staged
  // reply/anchor — an edit rewrites existing text, it isn't a new message.
  const beginEdit = (entry: ChatThreadEntry) => {
    setStagedReply(null);
    setSendFailed(false);
    setEditing({ id: entry.id, preview: entry.body });
    setDraft(entry.body);
    textareaRef.current?.focus();
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft('');
  };
  // Reply supersedes an in-progress edit: leaving an untouched edit to reply
  // instead must switch cleanly, so drop the edit (and its draft) first, then
  // stage the reply. Without this the composer stayed in edit mode and the
  // reply never took (the flow the user hit).
  const beginReply = (entry: ChatThreadEntry, label: string) => {
    if (editing) cancelEdit();
    setSendFailed(false);
    setStagedReply({ id: entry.id, label, preview: entry.body });
    textareaRef.current?.focus();
  };
  const confirmDelete = async () => {
    const id = deletingId;
    if (!id || !onDeleteMessage) return;
    setDeleteBusy(true);
    try {
      await onDeleteMessage(id);
      if (editing?.id === id) cancelEdit();
      setDeletingId(null);
    } catch {
      // Close the dialog so it doesn't sit open looking inert — the caller's
      // onDeleteMessage surfaces the reason (e.g. a "locked" toast). The
      // composer's sendFailed banner is hidden on locked threads, so relying
      // on it alone would leave a failed delete with no visible feedback.
      setDeletingId(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  const submit = async () => {
    if (readOnly || sendingRef.current) return;
    const t = draft.trim();
    if (!t) return;
    // Edit path: rewrite the staged message in place instead of sending new.
    if (editing) {
      if (!onEditMessage) return;
      // No change → just close the editor, don't call the BE (and don't stamp
      // an "edited" marker for a no-op edit).
      if (t === editing.preview.trim()) {
        cancelEdit();
        return;
      }
      sendingRef.current = true;
      setSendFailed(false);
      try {
        await onEditMessage(editing.id, t);
        setEditing(null);
        setDraft('');
      } catch {
        setSendFailed(true);
      } finally {
        sendingRef.current = false;
      }
      return;
    }
    if (!onAddComment) return;
    const anchor = stagedAnchor;
    const reply = stagedReply;
    const temp: ChatThreadEntry = {
      id: `optimistic-${Date.now()}`,
      actor: outgoingActor,
      body: t,
      anchor,
      reply_to: reply?.id,
      created_at: new Date().toISOString(),
    };
    sendingRef.current = true;
    setSendFailed(false);
    setOptimistic((prev) => [...prev, temp]);
    setDraft('');
    // Dismiss the staged anchor/reply in the SAME tick the optimistic
    // bubble goes up, not after the network round-trip resolves. The quote
    // is already captured into `temp` and visible in that bubble
    // immediately — leaving the staged chip up above the composer until
    // the request settled showed the same quote twice (once staged, once
    // already sent) for the round-trip's duration, and the chip's
    // disappearance visibly lagged the send instead of tracking it.
    // Trade-off: unlike the old after-the-await dismissal, a FAILED send
    // no longer leaves the quote re-attachable for a retry — the optimistic
    // bubble (and the quote inside it) is removed below same as before, so
    // there's nothing left showing it either way; the user re-selects the
    // text if they want to retry with the anchor attached. Passing the sent
    // anchor/reply (not a bare clear) still lets the owner keep any NEW
    // quote/reply the user staged in the instant between click and here.
    if (anchor) onDismissAnchor?.(anchor);
    setStagedReply((cur) => (cur === reply ? null : cur));
    try {
      await onAddComment({ body: t, anchor, replyTo: reply?.id });
    } catch {
      setOptimistic((prev) => prev.filter((e) => e.id !== temp.id));
      setDraft(t);
      setSendFailed(true);
    } finally {
      sendingRef.current = false;
    }
  };

  const composerDisabled = readOnly || isError || (isLoading && sorted.length === 0);
  const readOnlyTitle = readOnly ? 'You have read-only access to this campaign' : undefined;

  return (
    <section className={cn('flex h-full min-h-0 flex-1 flex-col bg-card', className)}>
      {/* WhatsApp-style "jump to original message": a soft tinted overlay
          fading to nothing over the bubble — not a border/ring, which reads
          as a validation state, not a momentary "you're here" pulse. */}
      <style>{`@keyframes chat-jump-flash { from { opacity: 0.35; } to { opacity: 0; } }`}</style>
      {/* Conversation header — Figma: 18px/500/#1B1A17 */}
      {title ? (
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
          <span className="text-heading-h7/7 font-medium text-[#1B1A17]">{title}</span>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close conversation"
              className="flex size-6 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
            >
              <X size={13} />
            </button>
          ) : null}
        </div>
      ) : null}

      <ScrollArea className="min-h-0 flex-1">
        <div ref={threadContentRef} className="px-[24px] py-[22px]">
          {isError ? (
            <ErrorState onRetry={onRetry} />
          ) : isLoading && sorted.length === 0 ? (
            <SkeletonBubbles />
          ) : sorted.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center text-body-sm-regular text-muted-foreground">
              <MessageCircle size={24} className="text-muted-foreground" />
              <p className="mt-2 max-w-xs text-caption-regular">{emptyStateText}</p>
            </div>
          ) : (
            (() => {
              const rendered: React.ReactNode[] = [];
              let lastDay = '';
              for (let i = 0; i < sorted.length; i += 1) {
                const e = sorted[i];
                if (!e) continue;
                const prev = i > 0 ? sorted[i - 1] : undefined;
                const next = i < sorted.length - 1 ? sorted[i + 1] : undefined;
                const k = dayKey(e.created_at);
                if (k !== lastDay) {
                  rendered.push(
                    <div
                      key={`day-${k}`}
                      className="my-4 flex items-center gap-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground first:mt-0"
                    >
                      <div className="h-px flex-1 bg-muted" />
                      <span>{formatDayLabel(e.created_at)}</span>
                      <div className="h-px flex-1 bg-muted" />
                    </div>,
                  );
                  lastDay = k;
                }

                if (e.kind === 'rejection') {
                  const rejectedByMe = isMine(e.actor, outgoingActor);
                  rendered.push(
                    <div key={e.id} className="my-3 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-rose-600 text-white">
                        <X size={16} strokeWidth={2.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-extrabold uppercase tracking-wider text-rose-900">
                          Rejected by {rejectedByMe ? 'You' : actorLabel(e.actor)}
                        </div>
                        <div className="mt-0.5 text-[11px] font-medium text-rose-700">
                          {formatTime(e.created_at)}
                        </div>
                        {e.body ? (
                          <p className="mt-1.5 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-rose-900">
                            {e.body}
                          </p>
                        ) : null}
                      </div>
                    </div>,
                  );
                  continue;
                }

                const mine = isMine(e.actor, outgoingActor);
                const parent = e.reply_to ? byId.get(e.reply_to) : undefined;
                const sameSenderAsPrev =
                  prev && prev.actor === e.actor && (prev.kind ?? '') === (e.kind ?? '') && dayKey(prev.created_at) === k;
                const sameSenderAsNext =
                  next && next.actor === e.actor && (next.kind ?? '') === (e.kind ?? '') && dayKey(next.created_at) === k;
                const isFirstOfGroup = !sameSenderAsPrev;
                const isLastOfGroup = !sameSenderAsNext;

                // Sender display name for label ("<name> · <org>") and the
                // avatar initial derived from it, so a name-less account shows
                // its brand initial rather than a role word's.
                const senderName = mine ? 'You' : senderDisplayName(e);
                const initial = mine ? 'Y' : (senderName.charAt(0) || '?').toUpperCase();

                // Per-message actions. Optimistic (not-yet-persisted) bubbles
                // have no real id to act on.
                const isOptimistic = String(e.id).startsWith('optimistic-');
                const canReplyMsg =
                  enableReplies && !!canAddComment && !!onAddComment && !readOnly && !isOptimistic;
                // Edit/Delete eligibility is entirely server-computed
                // (viewer_can_edit/viewer_can_delete — the permission matrix:
                // author-only edit within a seen+24h ceiling, plus role-scoped
                // delete overrides). The client only adds UI-level gates the
                // server check can't see: the lock/readOnly/optimistic state.
                const canEditMsg =
                  !!e.viewer_can_edit && !!onEditMessage && !threadLocked && !readOnly && !isOptimistic;
                // Super Admin's server-side delete is unconditional, lock
                // included (see viewerIsSuperAdmin doc comment) — every other
                // viewer still needs !threadLocked so the button doesn't
                // round-trip a 423.
                const canDeleteMsg =
                  !!e.viewer_can_delete &&
                  !!onDeleteMessage &&
                  (viewerIsSuperAdmin || !threadLocked) &&
                  !readOnly &&
                  !isOptimistic;
                const timeStr = formatTime(e.created_at);
                // Narrowed local, not e.anchor! at each use — a property
                // access doesn't stay narrowed inside the onClick/onKeyDown
                // closures below, a plain const does.
                const anchor = e.anchor;

                rendered.push(
                  <div
                    key={e.id}
                    className={cn(
                      'group flex items-end gap-2.5',
                      mine ? 'flex-row-reverse' : 'flex-row',
                      // Tight stack within a consecutive run from the same
                      // sender (Instagram/Telegram/WhatsApp-style); a real
                      // gap only between distinct groups.
                      isLastOfGroup ? 'mb-4' : 'mb-0.5',
                    )}
                  >
                    {/* Avatar (Figma 4635:90181/90183): 24px circle, dimmed
                        ground, sender photo when available — identical
                        treatment on both sides. Shown on last of group;
                        invisible placeholder otherwise for alignment. */}
                    <div
                      className={cn(
                        'size-6 shrink-0 overflow-hidden rounded-full bg-[#868e96] flex items-center justify-center text-[10px] font-medium text-white',
                        !isLastOfGroup && 'invisible',
                      )}
                      aria-hidden
                    >
                      {e.sender_avatar ? (
                        <img src={e.sender_avatar} alt="" className="size-full object-cover" />
                      ) : (
                        initial
                      )}
                    </div>

                    {/* Bubble column — max-w-[80%] lives HERE, not on the bubble
                        below. The column's own containing block (the message
                        row) has a definite width; the bubble's containing
                        block (this column) does not, since the column
                        shrink-to-fits its content. A percentage max-width on
                        the bubble would resolve against that self-referential
                        auto width — bubble ends up capped to 80% of its own
                        hugged size, which can be narrower than even a single
                        short word, forcing it to wrap for no reason. Capping
                        the column instead (against the row's real width) and
                        letting the bubble fill it (block default) breaks the
                        cycle. */}
                    <div
                      className={cn(
                        'flex min-w-0 max-w-[80%] flex-col',
                        mine ? 'items-end' : 'items-start',
                      )}
                    >
                      {/* Sender (first of group only) — the exact time now sits
                          inline on every bubble below, matching WhatsApp/
                          Telegram, so it isn't repeated up here. */}
                      {isFirstOfGroup ? (
                        <div className="mb-1 text-caption-regular/4 font-normal text-[#A3A3A3]">
                          {senderName}
                        </div>
                      ) : null}

                      {/* Bubble — relative so the hover-reveal reply icon
                          (below) can sit pinned to its bottom-right corner.
                          data-message-id is the target of jumpToMessage
                          (tapping a reply preview below). tabIndex={-1}:
                          not normally tab-reachable, but a valid target for
                          jumpToMessage's own target.focus() call so a
                          keyboard/AT user's focus actually moves here, not
                          just the scroll position. */}
                      <div
                        data-message-id={e.id}
                        tabIndex={-1}
                        className={cn(
                          'relative p-[12px] text-body-md-regular/6',
                          // A reply bubble must fit its quoted-parent preview.
                          parent && 'min-w-[228px]',
                          mine
                            ? 'rounded-[8px_8px_0px_8px] bg-[#F4ECFE] text-[#1A0540]'
                            : 'rounded-[8px_8px_8px_0px] bg-[#F4F4F5] text-[#000000]',
                        )}
                      >
                        {flashTarget?.id === e.id ? (
                          <div
                            key={flashTarget.nonce}
                            aria-hidden
                            className="pointer-events-none absolute inset-0 rounded-[inherit] bg-[#6C09F7]"
                            style={{ animation: 'chat-jump-flash 1.6s ease-out forwards' }}
                          />
                        ) : null}
                        {/* WhatsApp-style reply preview: who + what this message
                            answers, quoted inside the bubble on both sides.
                            bg-black/5 read as almost the same tone as both
                            bubble colors (#F4ECFE / #F4F4F5), so the quoted
                            card and the sender's own words below it blended
                            into one block — bg-white/70 gives it real
                            contrast against either, mb-2.5 a clearer gap.
                            Clickable — tapping it jumps to (and flashes) the
                            original message, same as WhatsApp. Silently
                            inert when the parent fell outside the loaded
                            page (no `parent`, nothing to scroll to). */}
                        {parent ? (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => jumpToMessage(parent.id)}
                            onKeyDown={(ev) => {
                              if (ev.key !== 'Enter' && ev.key !== ' ') return;
                              ev.preventDefault();
                              jumpToMessage(parent.id);
                            }}
                            aria-label="Jump to the original message"
                            className="mb-2.5 w-full min-w-[200px] cursor-pointer rounded-[6px] border-l-2 border-[#6C09F7] bg-white/70 px-2.5 py-1.5 text-left hover:bg-white/90"
                          >
                            <p className="text-[11px] font-semibold leading-4 text-[#5A07CE]">
                              {isMine(parent.actor, outgoingActor)
                                ? 'You'
                                : senderDisplayName(parent)}
                            </p>
                            {/* break-words, NOT wrap-anywhere: a short reply
                                ("haha") must not squeeze the preview into a
                                per-character column. whitespace-pre-wrap so
                                a multi-line parent message's own newlines
                                actually wrap across up to 2 lines here
                                instead of collapsing to one run of text —
                                same fix as the staged reply preview above
                                the composer. */}
                            <p className="line-clamp-2 whitespace-pre-wrap break-words text-[12px] leading-4 text-muted-foreground">
                              {parent.body}
                            </p>
                          </div>
                        ) : null}
                        {/* Quote-reply anchor: the script text this message is about,
                            visually separate from the sender's own words. */}
                        {anchor ? (
                          <div className="mb-2 w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[11px] py-2">
                            {anchor.aspect ? (
                              <div className="mb-1 flex items-center gap-1">
                                <span className="inline-block rounded-full border border-border bg-card px-2 py-px text-[10px] font-medium capitalize text-muted-foreground">
                                  {anchor.aspect.replace(/_/g, ' ')}
                                </span>
                              </div>
                            ) : null}
                            <p
                              role={onJumpToAnchor ? 'button' : undefined}
                              tabIndex={onJumpToAnchor ? 0 : undefined}
                              onClick={onJumpToAnchor ? () => onJumpToAnchor(anchor) : undefined}
                              onKeyDown={
                                onJumpToAnchor
                                  ? (ev) => {
                                      if (ev.key !== 'Enter' && ev.key !== ' ') return;
                                      ev.preventDefault();
                                      onJumpToAnchor(anchor);
                                    }
                                  : undefined
                              }
                              aria-label={onJumpToAnchor ? 'Jump to this line in the script' : undefined}
                              className={cn(
                                // whitespace-pre-wrap: a script anchor can
                                // span a real line break (e.g. a selection
                                // crossing a "Shot:"/"VO:" boundary) — same
                                // fix as the reply previews above.
                                // overflow-wrap:anywhere so a long unbroken
                                // token in the quoted text breaks and wraps
                                // instead of widening the bubble past the panel.
                                'whitespace-pre-wrap wrap-anywhere text-[14px] leading-5 text-[#171717]',
                                onJumpToAnchor && 'cursor-pointer rounded-sm hover:underline',
                                anchor.quoted_text.length > 180 &&
                                  !expandedMsgs.has(`${e.id}-anchor`) &&
                                  'line-clamp-4',
                              )}
                            >
                              &ldquo;{anchor.quoted_text}&rdquo;
                            </p>
                            {anchor.quoted_text.length > 180 ? (
                              <button
                                type="button"
                                onClick={() => toggleExpanded(`${e.id}-anchor`)}
                                className="mt-1 text-[11px] font-medium text-[#5A07CE] hover:underline"
                              >
                                {expandedMsgs.has(`${e.id}-anchor`) ? 'Show less' : 'Show more'}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                        {/* overflow-wrap:anywhere, NOT break-words: a single
                            long unbroken token (a URL, a pasted hash) must
                            break mid-word to wrap. break-words
                            (overflow-wrap:break-word) doesn't count the break
                            toward min-content sizing, so inside this flex
                            column the bubble kept its full token width and ran
                            off the panel in one strip. `anywhere` does count
                            it, letting the column's max-w-[80%] actually cap
                            and wrap the text. */}
                        <p
                          className={cn(
                            'whitespace-pre-wrap wrap-anywhere',
                            e.body.length > 260 && !expandedMsgs.has(e.id) && 'line-clamp-4',
                          )}
                        >
                          {e.body}
                        </p>
                        {e.body.length > 260 ? (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(e.id)}
                            className="mt-1 text-[13px] font-medium text-[#5A07CE] hover:underline"
                          >
                            {expandedMsgs.has(e.id) ? 'Show less' : 'Read more'}
                          </button>
                        ) : null}
                        {/* Per-bubble time, WhatsApp/Telegram-style — every
                            message carries its own timestamp, not just the
                            first of a consecutive group. */}
                        {timeStr || e.edited ? (
                          <div
                            className={cn(
                              'mt-1 text-right text-[10px] leading-none',
                              mine ? 'text-[#1A0540]/50' : 'text-muted-foreground',
                            )}
                          >
                            {e.edited ? <span className="italic">edited</span> : null}
                            {e.edited && timeStr ? ' · ' : null}
                            {timeStr}
                          </div>
                        ) : null}
                        {/* Reply — pinned to the bubble's own bottom corner,
                            hover-revealed (YouTube/Instagram-style comment
                            affordance), not a line item in the column's
                            normal flow. Industry-standard placement
                            (WhatsApp/Telegram/Messenger): the icon sits on
                            the bubble's INNER edge, toward the column's
                            center — bottom-left for a right-aligned "mine"
                            bubble, bottom-right for a left-aligned "theirs"
                            one — never pinned to a fixed screen side, which
                            would put it flush against the outer edge (past
                            the avatar) for one of the two directions. */}
                        {/* Hover action cluster on the bubble's inner-bottom
                            edge: Reply (all), Edit (server-granted), Delete
                            (server-granted). Grows toward the column centre so
                            it never runs past the outer edge/avatar. Each
                            button is size-6 for WCAG 2.5.8 target size. */}
                        {canReplyMsg || canEditMsg || canDeleteMsg ? (
                          <div
                            className={cn(
                              'absolute -bottom-2 z-10 flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
                              mine ? 'left-1' : 'right-1 flex-row-reverse',
                            )}
                          >
                            {canReplyMsg ? (
                              <button
                                type="button"
                                onClick={() => beginReply(e, senderName)}
                                aria-label="Reply"
                                title="Reply"
                                className="flex size-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
                              >
                                <ArrowBendUpLeft size={13} weight="bold" />
                              </button>
                            ) : null}
                            {canEditMsg ? (
                              <button
                                type="button"
                                onClick={() => beginEdit(e)}
                                aria-label="Edit message"
                                title="Edit"
                                className="flex size-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
                              >
                                <PencilSimple size={13} weight="bold" />
                              </button>
                            ) : null}
                            {canDeleteMsg ? (
                              <button
                                type="button"
                                onClick={() => setDeletingId(e.id)}
                                aria-label="Delete message"
                                title="Delete"
                                className="flex size-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:bg-rose-50 hover:text-rose-600"
                              >
                                <Trash size={13} weight="bold" />
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>,
                );
              }
              return rendered;
            })()
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      {readOnly || (canAddComment && onAddComment) ? (
        <div className="shrink-0 bg-card px-[18px] py-[14px] shadow-[0px_-1px_10px_rgba(0,0,0,0.1)]">
          {/* Staged quote-reply chip — dismissible, sits above the composer so
              the input itself stays empty for the sender's own words. */}
          {sendFailed ? (
            <p className="mb-1.5 text-[12px] font-medium text-rose-600">
              Couldn&rsquo;t send — please try again.
            </p>
          ) : null}
          {editing && !readOnly ? (
            // Mirrors the reply chip: a header line + a preview of the ORIGINAL
            // text (the message being edited), while the input below holds the
            // editable copy — WhatsApp-style.
            <div className="mb-2 flex items-start gap-2 rounded-[6px] border-l-2 border-[#6C09F7] bg-muted/60 px-2.5 py-2">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1 text-[11px] font-semibold text-[#5A07CE]">
                  <PencilSimple size={11} weight="bold" />
                  Editing message
                </p>
                <p className="line-clamp-2 whitespace-pre-wrap wrap-anywhere text-[12px] leading-4 text-muted-foreground">
                  {editing.preview}
                </p>
              </div>
              <button
                type="button"
                onClick={cancelEdit}
                aria-label="Cancel edit"
                className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
              >
                <X size={12} />
              </button>
            </div>
          ) : null}
          {stagedReply && !editing && !readOnly ? (
            // "Replying to You" read oddly for a reply to your own earlier
            // message — grammatically it addresses the reader, not the
            // target. Dropped the "Replying to" prefix entirely and matched
            // the accent border + purple sender-name styling the SAME quote
            // already gets once sent (see the parent-preview block above):
            // a small reply icon + bare sender name communicates "this is
            // what you're replying to" without needing it spelled out, and
            // the staged chip now looks like a preview of the sent one
            // instead of a differently-styled interim state.
            <div className="mb-2 flex items-start gap-2 rounded-[6px] border-l-2 border-[#6C09F7] bg-muted/60 px-2.5 py-2">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1 text-[11px] font-semibold text-[#5A07CE]">
                  <ArrowBendUpLeft size={11} weight="bold" />
                  {stagedReply.label}
                </p>
                {/* line-clamp-4, NOT a single truncated line — same as the
                    stagedAnchor quote chip below: full text stays in the
                    DOM and CSS does the multi-line clamp, so a short reply
                    isn't the only kind that stays readable at a glance.
                    whitespace-pre-wrap too — same as the actual sent bubble
                    body (see the message <p> below) — without it the
                    browser's default `white-space: normal` collapses a
                    real multi-line message's own newlines into one run of
                    text before line-clamp ever gets a chance to wrap it,
                    so a genuine 4-line message showed as a single
                    (wrong) line here instead of its real 4. */}
                <p className="line-clamp-4 whitespace-pre-wrap break-words text-[12px] leading-4 text-muted-foreground">
                  {stagedReply.preview}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStagedReply(null)}
                aria-label="Cancel reply"
                // size-6 (24px), not size-5 (20px) — WCAG 2.5.8 Target Size
                // minimum, and consistent with the Reply/close icon buttons
                // elsewhere in this file, which are already size-6.
                className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
              >
                <X size={12} />
              </button>
            </div>
          ) : null}
          {stagedAnchor && !readOnly ? (
            <div className="mb-2 flex items-start gap-2 rounded-[8px] border border-[#e5e5e5] bg-white px-[11px] py-2">
              <div className="min-w-0 flex-1">
                {stagedAnchor.aspect ? (
                  <span className="mb-0.5 inline-block rounded-full border border-border bg-muted px-2 py-px text-[10px] font-medium capitalize text-muted-foreground">
                    {stagedAnchor.aspect.replace(/_/g, ' ')}
                  </span>
                ) : null}
                <p className="line-clamp-4 whitespace-pre-wrap break-words text-[14px] leading-5 text-[#171717]">
                  &ldquo;{stagedAnchor.quoted_text}&rdquo;
                </p>
              </div>
              <button
                type="button"
                onClick={() => onDismissAnchor?.()}
                aria-label="Remove quoted text"
                // size-6 (24px), not size-5 (20px) — WCAG 2.5.8 Target Size
                // minimum, and consistent with the Reply/close icon buttons
                // elsewhere in this file, which are already size-6.
                className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
              >
                <X size={12} />
              </button>
            </div>
          ) : null}
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              rows={1}
              value={draft}
              disabled={composerDisabled}
              title={readOnlyTitle}
              onChange={(ev) => {
                setSendFailed(false);
                setDraft(ev.target.value);
              }}
              onKeyDown={(ev) => {
                // Enter sends; Shift+Enter inserts a newline (industry-
                // standard chat composer behavior — Slack, iMessage, etc).
                if (ev.key === 'Enter' && !ev.shiftKey) {
                  ev.preventDefault();
                  void submit();
                }
              }}
              placeholder={composerPlaceholder}
              className="max-h-[104px] min-h-10 flex-1 resize-none overflow-y-auto rounded-[6px] border border-[#E5E5E5] bg-card px-3 py-2.5 text-body-sm-medium/relaxed placeholder:text-[#737373] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
            />
            <Button
              type="button"
              variant="default"
              size="icon"
              disabled={!draft.trim() || !!addCommentBusy || composerDisabled}
              onClick={() => void submit()}
              aria-label="Send message"
              title={readOnlyTitle}
              className="size-10 shrink-0 rounded-full bg-[#18181B] text-white hover:bg-[#18181B]/90 enabled:hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {addCommentBusy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="shrink-0 bg-muted/60 px-4 py-3 text-center text-[11px] font-medium text-muted-foreground shadow-[0px_-1px_10px_rgba(0,0,0,0.1)]">
          This conversation is locked. History remains visible above.
        </div>
      )}

      {/* In-app delete confirmation (replaces the native window.confirm). */}
      <ActionConfirmDialog
        open={!!deletingId}
        onOpenChange={(next) => {
          if (!next) setDeletingId(null);
        }}
        variant="delete"
        title="Delete message?"
        description="This message will be permanently deleted for everyone. This can't be undone."
        confirmLabel="Delete"
        busy={deleteBusy}
        onConfirm={confirmDelete}
      />
    </section>
  );
}

function SkeletonBubbles() {
  const rows = [
    { mine: false, w: 'w-3/5' },
    { mine: true, w: 'w-2/5' },
    { mine: false, w: 'w-1/2' },
    { mine: true, w: 'w-3/5' },
    { mine: false, w: 'w-2/5' },
  ];
  return (
    <div className="flex flex-col gap-3" aria-hidden="true">
      {rows.map((r, i) => (
        <div key={i} className={cn('flex flex-col', r.mine ? 'items-end' : 'items-start')}>
          <Skeleton className="mb-1 h-3 w-20 rounded-sm" />
          <Skeleton
            className={cn(
              'h-10 rounded-[8px]',
              r.w,
              r.mine ? 'bg-secondary' : 'bg-muted',
            )}
          />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex h-full flex-col items-center justify-center text-center"
    >
      <div className="flex size-10 items-center justify-center rounded-full bg-rose-50 text-rose-500">
        <AlertCircle size={18} />
      </div>
      <p className="mt-3 text-body-sm-semibold text-foreground">Couldn't load thread</p>
      <p className="mt-1 max-w-xs text-caption-regular text-muted-foreground">
        Something went wrong fetching messages. Check your connection and try again.
      </p>
      {onRetry ? (
        <Button
          type="button"
          variant="outline"
          onClick={onRetry}
          className="mt-4 h-auto rounded-full px-3 py-1.5 text-caption-medium"
        >
          <RefreshCw size={12} />
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export default ChatThread;
